import { day, hasValue } from '@vrplatform/utils';
import { Popover } from '@mantine/core';
import { type DateValue, MonthPicker } from '@mantine/dates';
import { useEffect, useMemo, useState } from 'react';
import { FilterPill } from './FilterPill';

type BaseProps = {
  label: string;
  numberOfColumns?: number;
  columnsToScroll?: number;
  minDate?: string;
  maxDate?: string;
  defaultDisplayedDate?: string;
};

type ValueProps =
  | {
      value: [DateValue, DateValue] | undefined | null;
      setValue: (v: [DateValue, DateValue] | undefined | null) => void;
      type: 'range';
      labelFormatter?: (
        value: [DateValue, DateValue] | undefined | null
      ) => string;
    }
  | {
      value: DateValue | undefined | null;
      setValue: (v: DateValue | undefined | null) => void;
      type: 'default';
      labelFormatter?: (value: DateValue | undefined | null) => string;
    };

type Props = BaseProps & ValueProps;

export const MonthFilter = ({
  label: defaultLabel,
  value,
  setValue,
  columnsToScroll = 1,
  numberOfColumns = 2,
  type,
  maxDate,
  minDate,
  labelFormatter,
  defaultDisplayedDate,
}: Props) => {
  const [input, setInput] = useState(value);
  const [opened, setOpened] = useState(false);

  const isDefault = useMemo<boolean>(() => {
    if (type === 'range') return !value || value.filter(hasValue).length === 0;

    return !value;
  }, [value, type]);

  const onChange = (v: boolean) => {
    setOpened(v);

    if (input !== value) setValue(input as any);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (input !== value) setInput(value);
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const label = useMemo(() => {
    if (isDefault) return defaultLabel;

    if (type === 'range') {
      const i = input as [DateValue, DateValue] | undefined | null;

      if (labelFormatter) return labelFormatter(i);
      const startDate = i?.[0] ? day(i[0]) : null;
      const endDate = i?.[1] ? day(i[1]) : null;

      const showStartDateYear =
        !endDate || startDate?.year() !== endDate?.year();

      const isSameMonth =
        startDate?.month() === endDate?.month() &&
        startDate?.year() === endDate?.year();

      if (isSameMonth) return startDate?.format('MMMM YYYY') || '-';

      const isFullYear =
        startDate &&
        endDate &&
        startDate.year() === endDate.year() &&
        startDate.yyyymmdd() === startDate.startOf('year').yyyymmdd() &&
        endDate.yyyymmdd() === endDate.endOf('year').yyyymmdd();

      if (isFullYear) return startDate?.format('YYYY') || '-';

      return [
        startDate?.format(showStartDateYear ? 'MMMM YYYY' : 'MMMM'),
        endDate?.format('MMMM YYYY'),
      ]
        .filter(hasValue)
        .join(' - ');
    } else {
      const i = input as DateValue | undefined | null;
      if (labelFormatter) return labelFormatter(i);
      if (!i) return defaultLabel;
      return day(i).format('MMMM YYYY');
    }
  }, [value, defaultLabel, isDefault, type, labelFormatter]);

  return (
    <Popover
      opened={opened}
      onChange={onChange}
      position="bottom-start"
      radius="md"
      shadow="md"
    >
      <Popover.Target>
        <FilterPill
          aria-label={label || defaultLabel}
          opened={opened}
          isDefault={isDefault}
          onClick={() => setOpened((v) => !v)}
          label={label}
          resetFilter={undefined}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <MonthPicker
          type={type}
          numberOfColumns={numberOfColumns}
          columnsToScroll={columnsToScroll}
          nextLabel="Next period"
          previousLabel="Previous period"
          value={input || (type === 'range' ? [null, null] : null)}
          onChange={type === 'default' ? (setValue as any) : setInput}
          color="primary"
          minDate={minDate ? day(minDate).toDate() : undefined}
          maxDate={maxDate ? day(maxDate).toDate() : undefined}
          styles={(theme) => ({
            monthsListControl: {
              '&:hover': {
                backgroundColor: theme.colors[theme.primaryColor][0] + 20,
              },
              '&[data-selected]': {
                backgroundColor: theme.colors[theme.primaryColor][6],
                '&:hover': {
                  backgroundColor: theme.colors[theme.primaryColor][7],
                },
              },
            },
          })}
          defaultDate={
            defaultDisplayedDate
              ? day(defaultDisplayedDate).toDate()
              : undefined
          }
        />
      </Popover.Dropdown>
    </Popover>
  );
};
